import React, { FC, useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';

import { MagicLinkInviteSimplified } from '@/components/MagicLinkInvite';
import { ONBOARDING_INVITE_TEAM_FORM_ID } from '@/components/MainModal/onboardingInviteFormIds';
import {
  OnboardingModalActions,
  useOnboardingModalActionsCompact,
} from '@/components/MainModal/OnboardingModalActions';
import { useInviteTeamLaterButtonLabel } from '@/components/MainModal/useInviteTeamLaterButtonLabel';
import { useSendInvitesButtonLabel } from '@/components/MainModal/useSendInvitesButtonLabel';
import { useSkipStepButtonLabel } from '@/components/MainModal/useSkipStepButtonLabel';
import { InviteOptionsProps, InviteStepCtx } from '@/components/Steps';
import { AddMoreButton } from '@/components/Steps/InviteTeamStep/AddMoreBtn';
import { useOnboardingTask, useTracking } from '@/hooks';
import { Button, ButtonProps, LoadingButton } from '@/shared/Button';
import { FlexBox } from '@/shared/FlexBox';
import { Tooltip } from '@/shared/Tooltip';

import { useDownloadApps } from '../../../hooks/useDownloadApps';

/** Muted outline (was "invite later" in B; paired with primary outlined copy before swap). */
const INVITE_VARIANT_B_COPY_LINK_OUTLINED_SX = {
  color: '#9A918D',
  borderColor: '#DDDBD9',
  '&:hover': {
    borderColor: '#DDDBD9',
    backgroundColor: 'rgba(154, 145, 141, 0.04)',
  },
} as const;

/** Primary outlined emphasis (was copy link in B; used for "invite team later" after swap). */
const INVITE_VARIANT_B_INVITE_LATER_OUTLINED_SX = {
  fontSize: '14px !important',
  height: '36px',
  flex: '1 1 auto',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '0.75px',
  borderColor: '#DDDBD9',
  '&:hover': {
    borderColor: '#DDDBD9',
  },
} as const;

export const InviteOptions: FC<InviteOptionsProps> = ({
  flow,
  membersToInvite,
  onAddMoreClick,
  onRemoveMemberClick,
  onBackClick,
  onSendInvites,
  renderMemberField,
  inviteOptimizationVariant,
  isAutomaticTracking = false,
}) => {
  const { t } = useTranslation('onboarding');
  const compactActions = useOnboardingModalActionsCompact();
  const skipStepButtonLabel = useSkipStepButtonLabel();
  const inviteTeamLaterButtonLabel = useInviteTeamLaterButtonLabel();
  const sendInvitesButtonLabel = useSendInvitesButtonLabel();
  const { goToNextStep } = useOnboardingTask(flow);
  const { didCopyLink } = useContext(InviteStepCtx);
  const { trackOnboardingStep } = useTracking(flow);
  const { control, formState: { isSubmitting, isValid, isDirty }} = useFormContext();
  const watchedEmails = useWatch({ control });
  const { executeBrowserPromoTracking } = useDownloadApps(flow);

  const v = inviteOptimizationVariant ?? 'A';
  const isVariantA = v === 'A';
  const isVariantB = v === 'B';
  const isVariantC = v === 'C';

  const hasAnyEmail = membersToInvite.some((member) => {
    const val = watchedEmails?.[`email-${ member.id }`];
    return typeof val === 'string' && val.trim().length > 0;
  });

  const showSendButton = isAutomaticTracking
    || isVariantA
    || isVariantC
    || (isVariantB && hasAnyEmail);

  const sendDisabled = isVariantC || isAutomaticTracking
    ? (!isDirty || !isValid)
    : ((!isDirty || !isValid) && !didCopyLink);

  const tooltipDisableHover = isVariantC || isAutomaticTracking
    ? Boolean(isValid && isDirty)
    : Boolean((isValid && isDirty) || didCopyLink);

  const handleSkipBtnClick = async () => {
    await executeBrowserPromoTracking();
    await trackOnboardingStep('Skipped Invite User during Onboarding');
    goToNextStep();
  };

  const handleInviteTeamLaterVariantC = async () => {
    await executeBrowserPromoTracking();
    await trackOnboardingStep('Skipped Invite User during Onboarding');
    goToNextStep({ withNextStepId: 'inviteLinkShareStep' });
  };

  let variantBSecondaryAction: React.ReactNode = null;
  if (isVariantB && !isAutomaticTracking) {
    if (didCopyLink && !hasAnyEmail) {
      variantBSecondaryAction = (
        <Button
          variant="contained"
          color="primary"
          size="medium"
          uppercase
          onClick={ handleSkipBtnClick }
          sx={{ fontSize: '14px !important', height: '36px', flex: '1 1 auto' }}
        >
          { t('onboarding:invite_members_step.optimization_b.go_to_dashboard_btn') }
        </Button>
      );
    } else if (!didCopyLink && !hasAnyEmail) {
      variantBSecondaryAction = (
        <Box flex="1 1 auto">
          <Button
            variant="outlined"
            size="medium"
            uppercase
            onClick={ handleSkipBtnClick }
            sx={ INVITE_VARIANT_B_INVITE_LATER_OUTLINED_SX }
          >
            { inviteTeamLaterButtonLabel }
          </Button>
        </Box>
      );
    }
  }

  return (
    <form
      id={ ONBOARDING_INVITE_TEAM_FORM_ID }
      className="d-flex-col"
      onSubmit={ onSendInvites } noValidate
      aria-label="Invite team by email"
      data-invite-step-optimization={ inviteOptimizationVariant }
      style={{ height: '100%', rowGap: '36px' }}
    >
      <Box>
        <FlexBox column rowGap={ 3 } mb={ 2 }>
          { membersToInvite.map((member, i) => (
            <Box
              key={ member.id }
              display="flex"
              alignItems="center"
              gap={ 1 }
              sx={{ position: 'relative' }}
            >
              <Box flex={ 1 }>
                { renderMemberField(member, i) }
              </Box>

              { i > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '16px',
                    right: '-22px',
                    opacity: 1,
                  }}
                >
                  <Tooltip title="Remove" arrow>
                    <IconButton
                      size="large"
                      color="error"
                      onClick={ () => onRemoveMemberClick(member.id) }
                      sx={{
                        p: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 'large' as const }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) }
            </Box>
          )) }
        </FlexBox>
        <AddMoreButton onClick={ onAddMoreClick } variant="exp" />
      </Box>

      <OnboardingModalActions>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={ compactActions ? 0 : 'auto' }
          flexDirection="row"
          flexWrap="wrap"
          gap={ 2 }
        >
          <Button
            variant="outlined"
            size="medium"
            uppercase
            onClick={ onBackClick }
            sx={{ fontSize: '14px !important', height: '36px' }}
          >
            { t('common:back') }
          </Button>

          <Box display="flex" gap={ 2 } flexDirection="row" alignItems="center" flexWrap="wrap">
            { (isVariantA || isAutomaticTracking) && (
              <RemindMeLaterBtn onClick={ handleSkipBtnClick }>
                { skipStepButtonLabel }
              </RemindMeLaterBtn>
            ) }

            { isVariantA && !isAutomaticTracking && (
              <MagicLinkInviteSimplified />
            ) }

            { isVariantB && !isAutomaticTracking && (
              <>
                { variantBSecondaryAction }
                <MagicLinkInviteSimplified
                  linkCopiedSuccessMessage={ t('onboarding:invite_members_step.optimization_b.toast_link_copied') }
                  copyButtonLabel={ t('onboarding:invite_members_step.optimization_b.copy_invite_link') }
                  copyButtonSx={ INVITE_VARIANT_B_COPY_LINK_OUTLINED_SX }
                  linkIconSx={{ color: '#9A918D' }}
                />
              </>
            ) }

            { isVariantC && !isAutomaticTracking && (
              <Box flex="1 1 auto">
                <RemindMeLaterBtn onClick={ handleInviteTeamLaterVariantC }>
                  { inviteTeamLaterButtonLabel }
                </RemindMeLaterBtn>
              </Box>
            ) }

            { showSendButton && (
              <Tooltip
                title={
                  isAutomaticTracking
                    ? t('onboarding:invite_members_step.automatic_tracking.tooltip_hover_btn')
                    : isVariantC
                      ? t('onboarding:invite_members_step.tooltip_hover_btn.message_c')
                      : t('onboarding:invite_members_step.tooltip_hover_btn.message')
                }
                placement="top"
                disableFocusListener
                disableHoverListener={ tooltipDisableHover }
                enterTouchDelay={ 0 }
                tooltipSx={{
                  backgroundColor: '#30302F',
                  color: '#FFFFFF',
                }}
              >
                <Box flex="1 1 auto" height={ '36px' }>
                  <LoadingButton
                    form={ ONBOARDING_INVITE_TEAM_FORM_ID }
                    variant="contained"
                    type="submit"
                    disableElevation={ false }
                    loading={ isSubmitting }
                    disabled={ sendDisabled }
                    fullWidth
                    sx={{ fontSize: '14px' }}
                    css={ css`
                      &.Mui-disabled {
                        cursor: not-allowed;
                        pointer-events: auto;
                      }
                    ` }
                  >
                    { isVariantC || isVariantB
                      ? sendInvitesButtonLabel
                      : t('common:send') }
                  </LoadingButton>
                </Box>
              </Tooltip>
            ) }
          </Box>
        </Box>
      </OnboardingModalActions>
    </form>
  )
  ;
};

const RemindMeLaterBtn = styled((props: ButtonProps) =>
  <Button variant="outlined" { ...props } />)(() => css`
  display: block;
  flex: 1 1 auto;
  color: #9A918D;
  font-size: 14px;
`);
